import { Application } from '@/types/application';
import { Detail, emptyValue } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { VariableRef } from '@/types/variable';
import { isEmptyValue, isNullOrUndefined, isNumber, isObject, isString, isVariable } from '@/util/typeGuards';
import { variableUtil } from './variable';

export const valueUtil = (): ValidationObject<VariableRef | string | number | emptyValue | undefined> => {
  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    const value = getValue(configuration, details);
    if (isVariable(value)) {
      const validatedVariable = variableUtil().validate(
        application,
        metadata,
        linkedSubroutineId,
        nodeId,
        blockName,
        configuration,
        details
      );
      if (validatedVariable.length) {
        errors.push(...validatedVariable);
      }
    }
    if (configuration.required && !isValid(value)) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is missing a required value: ${configuration.label}`,
        id: nodeId
      });
    }
    return errors;
  };

  /**
   * Returns true if it has an actual value (empty string is not valid but the number zero is)
   */
  const isValid = (value: string | number | object | VariableRef | undefined): boolean => {
    return (
      !isNullOrUndefined(value) &&
      (isNumber(value) || (isString(value) && !!value) || isVariable(value) || isEmptyValue(value))
    );
  };

  const getValue = (
    configuration: Configuration,
    initVal: unknown
  ): VariableRef | string | number | emptyValue | undefined => {
    if (isNullOrUndefined(initVal)) return;

    if (isVariable(initVal) || isString(initVal) || isNumber(initVal) || isEmptyValue(initVal)) {
      return initVal;
    }
    if (isObject(initVal)) {
      const prop = initVal[configuration.value];
      return getValue(configuration, prop);
    }
    return;
  };

  return {
    validate: validate,
    getValue: getValue
  };
};
